<!-- Bandeau d'introduction -->
<section class="relative isolate flex min-h-100 items-center overflow-hidden bg-(--pnud-bleu) text-white sm:min-h-115 lg:min-h-160">
  <!-- Fond plein cadre, purement décoratif : voile foncé par-dessus pour garder le texte blanc lisible. -->
  <div class="absolute inset-0" aria-hidden="true">
    <img [ngSrc]="heroImage" alt="" fill priority sizes="100vw" class="object-cover object-[40%_60%]" />
    <div class="absolute inset-0 bg-linear-to-r from-[rgba(11,42,68,0.48)] via-[rgba(11,42,68,0.35)] to-[rgba(11,42,68,0.15)]"></div>
  </div>

  <div class="relative mx-auto w-full max-w-7xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
    <div class="relative pl-6">
      <div class="absolute inset-y-0 left-0 w-1">
        <div class="h-1/3 w-full bg-(--cmr-vert)"></div>
        <div class="h-1/3 w-full bg-(--cmr-rouge)"></div>
        <div class="h-1/3 w-full bg-(--cmr-jaune)"></div>
      </div>
      <div class="hero-in text-xs font-bold tracking-[.12em] text-(--cmr-jaune)">{{ 'pages.about.hero.eyebrow' | translate }}</div>
      <h1 class="hero-in hero-in-2 mt-3 max-w-3xl text-3xl font-extrabold leading-tight sm:text-4xl">
        {{ 'pages.about.section.title' | translate }}
      </h1>
      <p class="hero-in hero-in-3 mt-5 max-w-2xl text-[15px] leading-relaxed text-white/80 sm:text-[16.5px]">
        {{ 'pages.about.hero.subtitle' | translate }}
      </p>
    </div>
  </div>
</section>

<!-- Youth Connekt Africa : l'initiative panafricaine dont Youth Connekt Cameroon est la déclinaison nationale. -->
<section class="bg-(--surface-subtle) py-12 sm:py-14">
  <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
    <div class="grid items-start gap-8 lg:grid-cols-[1.4fr_1fr]" appScrollReveal>
      <div>
        <div class="text-xs font-bold tracking-[.12em] text-(--text-muted)">{{ 'pages.about.africa.eyebrow' | translate }}</div>
        <h2 class="mt-1.5 text-2xl font-extrabold text-(--marine)">{{ 'pages.about.africa.title' | translate }}</h2>
        <p class="mt-4 text-[14.5px] leading-relaxed text-(--text-body)">
          {{ 'pages.about.africa.paragraph1' | translate }}
        </p>
        <p class="mt-3.5 text-[14.5px] leading-relaxed text-(--text-body)">
          {{ 'pages.about.africa.paragraph2' | translate }}
        </p>
        <p class="mt-3.5 text-[14.5px] font-semibold leading-relaxed text-(--marine)">
          {{ 'pages.about.africa.paragraph3' | translate }}
        </p>
      </div>

      <div class="box-border rounded-md bg-(--pnud-bleu) p-6 text-white">
        <div>
          <div class="text-[11px] font-extrabold tracking-[.1em] text-white/60">{{ 'pages.about.africa.missionLabel' | translate }}</div>
          <p class="mt-2 text-[13.5px] leading-relaxed text-white/85">
            {{ 'pages.about.africa.missionText' | translate }}
          </p>
        </div>
        <div class="mt-5 border-t border-white/15 pt-4">
          <div class="text-[11px] font-extrabold tracking-[.1em] text-white/60">{{ 'pages.about.africa.visionLabel' | translate }}</div>
          <p class="mt-2 text-[13.5px] leading-relaxed text-white/85">
            {{ 'pages.about.africa.visionText' | translate }}
          </p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Gouvernance du programme -->
<section class="bg-white py-12 sm:py-14">
  <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
    <div class="mb-8" appScrollReveal>
      <div class="text-xs font-bold tracking-[.12em] text-(--text-muted)">{{ 'pages.about.governance.eyebrow' | translate }}</div>
      <h2 class="mt-1.5 text-2xl font-extrabold text-(--marine)">{{ 'pages.about.governance.title' | translate }}</h2>
    </div>

    <div class="mx-auto grid max-w-3xl gap-5 sm:grid-cols-2">
      @for (member of governance(); track member.name; let i = $index) {
        <article [appScrollReveal]="i"
          class="group overflow-hidden rounded-2xl border border-(--border-subtle) bg-white shadow-(--shadow-card) transition duration-300 hover:-translate-y-1 hover:shadow-lg">
          <div class="aspect-5/5 w-full overflow-hidden bg-(--gris-100)">
            <img [ngSrc]="member.photo" [alt]="member.name" width="800" height="1000"
              class="h-full w-full object-cover object-top transition duration-300 group-hover:scale-105" />
          </div>
          <div class="p-5 sm:p-6">
            <div class="text-[11px] font-extrabold tracking-[.12em] text-(--text-faint)">{{ member.badgeLabel }}</div>
            <div class="mt-1.5 text-[17px] font-extrabold leading-tight text-(--marine)">{{ member.name }}</div>
            <div class="mt-1 text-[13px] font-bold text-(--text-link)">{{ member.role }}</div>
            <p class="mt-3 text-[13px] leading-relaxed text-(--text-body)">{{ member.description }}</p>
          </div>
        </article>
      }
    </div>
  </div>
</section>

<!-- La Direction Nationale + résultats -->
<section class="bg-(--surface-subtle) py-12 sm:py-14">
  <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
    <div class="grid items-start gap-8 lg:grid-cols-[1.4fr_1fr]" appScrollReveal>
      <div>
        <div class="text-xs font-bold tracking-[.12em] text-(--text-muted)">{{ 'pages.about.direction.eyebrow' | translate }}</div>
        <h2 class="mt-1.5 text-2xl font-extrabold text-(--marine)">{{ 'pages.about.direction.title' | translate }}</h2>
        <p class="mt-4 text-[14.5px] leading-relaxed text-(--text-body)">
          {{ 'pages.about.direction.paragraph1' | translate }}
        </p>
        <p class="mt-3.5 text-[14.5px] leading-relaxed text-(--text-body)">
          {{ 'pages.about.direction.paragraph2' | translate }}
        </p>
      </div>

      <div class="box-border rounded-md bg-(--pnud-bleu) p-6 text-white">
        <div class="text-[11px] font-extrabold tracking-[.1em] text-white/60">{{ 'pages.about.direction.resultsLabel' | translate }}</div>
        <div class="mt-3 flex flex-col gap-3.5">
          @for (figure of keyFigures(); track figure.label) {
            <div>
              <!-- text-white plutôt que --ycc-bleu-ciel : ce bleu ciel sur le fond --pnud-bleu de
                   l'encadré tombait à ~1.6:1 de contraste (échoue largement l'AA, 3:1 minimum même
                   pour du texte large) — signalé par un utilisateur comme peu lisible. Blanc plein,
                   cohérent avec le reste des textes de l'encadré (déjà en blanc à opacité réduite),
                   sans introduire de nouvelle teinte vive. -->
              <div class="text-[28px] leading-none font-extrabold text-white">{{ figure.value }}</div>
              <div class="mt-1 text-[13px] text-white/75">{{ figure.label }}</div>
            </div>
          }
        </div>
        <p class="mt-4 border-t border-white/15 pt-3 text-[11.5px] text-white/50">
          {{ 'pages.about.direction.resultsSource' | translate }}
        </p>
      </div>
    </div>
  </div>
</section>

<!-- Missions -->
<section class="bg-white py-12 sm:py-14">
  <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
    <div class="mb-8 max-w-2xl" appScrollReveal>
      <div class="text-xs font-bold tracking-[.12em] text-(--text-muted)">{{ 'pages.about.missions.eyebrow' | translate }}</div>
      <h2 class="mt-1.5 text-2xl font-extrabold text-(--marine)">{{ 'pages.about.missions.title' | translate }}</h2>
      <p class="mt-3 text-[14.5px] leading-relaxed text-(--text-body)">
        {{ 'pages.about.missions.subtitle' | translate }}
      </p>
    </div>

    <div class="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      @for (mission of missions(); track mission.title; let i = $index) {
        <article [appScrollReveal]="i"
          class="group box-border rounded-md border border-(--border-subtle) bg-white p-5 shadow-(--shadow-card) transition duration-300 hover:-translate-y-1 hover:border-transparent hover:shadow-lg">
          <span class="inline-flex h-10 w-10 items-center justify-center rounded-md transition duration-300 group-hover:scale-110"
            [style.background]="mission.bg" [style.color]="mission.fg">
            <i class="pi {{ mission.icon }} text-[18px]" aria-hidden="true"></i>
          </span>
          <h3 class="mt-3.5 text-[15px] font-extrabold text-(--marine)">{{ mission.title }}</h3>
          <p class="mt-1.5 text-[13px] leading-relaxed text-(--text-body)">{{ mission.description }}</p>
        </article>
      }
    </div>
  </div>
</section>

<!-- Événements phares -->
<section class="bg-(--surface-subtle) py-12 sm:py-14">
  <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
    <div class="mb-8" appScrollReveal>
      <div class="text-xs font-bold tracking-[.12em] text-(--text-muted)">{{ 'pages.about.flagship.eyebrow' | translate }}</div>
      <h2 class="mt-1.5 text-2xl font-extrabold text-(--marine)">{{ 'pages.about.flagship.title' | translate }}</h2>
    </div>

    <div class="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      @for (event of flagshipEvents(); track event.title; let i = $index) {
        <article role="button" tabindex="0" [attr.aria-label]="'pages.about.flagship.learnMoreAria' | translate: { title: event.title }"
          (click)="openFlagshipEvent(event)" (keydown.enter)="openFlagshipEvent(event)"
          (keydown.space)="$event.preventDefault(); openFlagshipEvent(event)"
          [appScrollReveal]="i"
          class="group box-border flex h-full cursor-pointer flex-col rounded-md border border-(--border-subtle) bg-white p-5 shadow-(--shadow-card) transition duration-300 hover:-translate-y-1 hover:border-transparent hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--pnud-bleu)">
          <div class="mb-3 h-1 w-8 rounded transition-all duration-300 group-hover:w-12" [style.background]="event.accent"></div>
          <h3 class="text-[15px] font-extrabold text-(--marine)">{{ event.title }}</h3>
          <p class="mt-1.5 text-[13px] leading-relaxed text-(--text-body)">{{ event.description }}</p>
          <!-- mt-auto : ancre "En savoir plus" en bas de la carte, alignée sur la même ligne d'une
               carte à l'autre malgré des titres/descriptions de longueurs différentes (voir h-full
               + flex-col ci-dessus, qui étirent chaque carte à la hauteur de la ligne de la grille). -->
          <div class="mt-auto pt-3 inline-flex items-center gap-1.5 text-[12.5px] font-extrabold text-(--pnud-bleu)">
            {{ 'pages.about.flagship.learnMore' | translate }}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </div>
        </article>
      }
    </div>
  </div>
</section>

@if (selectedFlagshipEvent(); as ev) {
  <div class="fixed inset-0 z-70 flex items-center justify-center bg-slate-950/70 p-4" (click)="closeFlagshipEvent()">
    <div
      class="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-[10px] bg-white shadow-2xl"
      pFocusTrap
      [appModalDialog]="'flagship-event-modal-title'"
      (closed)="closeFlagshipEvent()"
      (click)="$event.stopPropagation()"
    >
      <div class="h-1.25 shrink-0" [style.background]="ev.accent"></div>
      <div class="flex shrink-0 items-start justify-between gap-4 p-6 pb-0">
        <h3 id="flagship-event-modal-title" class="m-0 text-[20px] font-extrabold leading-tight text-(--marine)">
          {{ ev.title }}
        </h3>
        <button type="button" (click)="closeFlagshipEvent()" [attr.aria-label]="'common.close' | translate"
          class="inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-(--text-muted) transition hover:bg-slate-100">
          <i class="pi pi-times" aria-hidden="true"></i>
        </button>
      </div>

      <div class="min-h-0 overflow-y-auto p-6">
        <p class="m-0 mb-6 text-[14px] leading-relaxed text-(--text-body)">{{ ev.description }}</p>

        <div class="rounded-md bg-(--surface-subtle) p-5">
          @if (ev.objectives.length > 0) {
            <div>
              <h4 class="m-0 mb-2.5 text-[14px] font-extrabold text-(--marine)">{{ 'pages.about.flagship.objectivesTitle' | translate }}</h4>
              <ul class="m-0 flex list-none flex-col gap-2 p-0">
                @for (point of ev.objectives; track point) {
                  <li class="flex items-start gap-2.5 text-[13.5px] leading-relaxed text-(--text-body)">
                    <span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" [style.background]="ev.accent"></span>
                    {{ point }}
                  </li>
                }
              </ul>
            </div>
          }

          @if (ev.activities.length > 0) {
            <div class="mt-6">
              <h4 class="m-0 mb-3 text-[14px] font-extrabold text-(--marine)">{{ 'pages.about.flagship.activitiesTitle' | translate }}</h4>
              <div class="flex flex-col gap-4">
                @for (activity of ev.activities; track activity.title) {
                  <div>
                    <h5 class="m-0 mb-2 text-[13.5px] font-extrabold text-(--text-link)">{{ activity.title }}</h5>
                    <ul class="m-0 flex list-none flex-col gap-2 p-0">
                      @for (point of activity.points; track point) {
                        <li class="flex items-start gap-2.5 text-[13.5px] leading-relaxed text-(--text-body)">
                          <span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" [style.background]="ev.accent"></span>
                          {{ point }}
                        </li>
                      }
                    </ul>
                  </div>
                }
              </div>
            </div>
          }

          @if (ev.impact.length > 0) {
            <div class="mt-6">
              <h4 class="m-0 mb-2.5 text-[14px] font-extrabold text-(--marine)">{{ 'pages.about.flagship.impactTitle' | translate }}</h4>
              @if (ev.impactIntro) {
                <p class="m-0 mb-2.5 text-[13.5px] leading-relaxed text-(--text-body)">{{ ev.impactIntro }}</p>
              }
              <ul class="m-0 flex list-none flex-col gap-2 p-0">
                @for (point of ev.impact; track point) {
                  <li class="flex items-start gap-2.5 text-[13.5px] leading-relaxed text-(--text-body)">
                    <span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" [style.background]="ev.accent"></span>
                    {{ point }}
                  </li>
                }
              </ul>
            </div>
          }

          @if (ev.whyParticipate.length > 0) {
            <div class="mt-6">
              <h4 class="m-0 mb-2.5 text-[14px] font-extrabold text-(--marine)">{{ 'pages.about.flagship.whyParticipateTitle' | translate }}</h4>
              <ul class="m-0 flex list-none flex-col gap-2 p-0">
                @for (point of ev.whyParticipate; track point) {
                  <li class="flex items-start gap-2.5 text-[13.5px] leading-relaxed text-(--text-body)">
                    <span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" [style.background]="ev.accent"></span>
                    {{ point }}
                  </li>
                }
              </ul>
            </div>
          }
        </div>
      </div>
    </div>
  </div>
}

<!-- Objectifs -->
<section class="bg-white py-12 sm:py-14">
  <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
    <div class="mb-8" appScrollReveal>
      <div class="text-xs font-bold tracking-[.12em] text-(--text-muted)">{{ 'pages.about.objectives.eyebrow' | translate }}</div>
      <h2 class="mt-1.5 text-2xl font-extrabold text-(--marine)">{{ 'pages.about.objectives.title' | translate }}</h2>
    </div>

    <div class="grid gap-8 sm:grid-cols-3">
      @for (objective of objectives(); track objective.number; let i = $index) {
        <div [appScrollReveal]="i">
          <div class="text-[26px] font-extrabold" [style.color]="objective.accent">{{ objective.number }}</div>
          <h3 class="mt-1.5 text-[15px] font-extrabold text-(--marine)">{{ objective.title }}</h3>
          <p class="mt-1.5 text-[13.5px] leading-relaxed text-(--text-body)">{{ objective.description }}</p>
        </div>
      }
    </div>
  </div>
</section>

@if (!isAuthenticated()) {
  <!-- Appel à l'action : n'a de sens que pour un visiteur non connecté, un compte existant n'a rien à "rejoindre". -->
  <section class="bg-(--pnud-bleu) py-10 sm:py-12">
    <div class="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-5 px-4 sm:px-6 lg:px-8" appScrollReveal>
      <div>
        <h2 class="m-0 text-xl font-extrabold text-white sm:text-2xl">{{ 'pages.about.cta.title' | translate }}</h2>
        <p class="mt-1.5 max-w-xl text-[14.5px] text-white/85">
          {{ 'pages.about.cta.subtitle' | translate }}
        </p>
      </div>
      <div class="flex flex-wrap gap-3">
        <a routerLink="/auth/register"
          class="box-border inline-flex h-11 items-center justify-center rounded-sm bg-white px-5 text-[14px] font-bold text-(--pnud-bleu) transition-colors hover:bg-white/90">
          {{ 'nav.register' | translate }}
        </a>
        <a routerLink="/opportunities"
          class="box-border inline-flex h-11 items-center justify-center rounded-sm border border-white px-5 text-[14px] font-bold text-white transition-colors hover:bg-white/10">
          {{ 'pages.about.cta.viewOpportunities' | translate }}
        </a>
      </div>
    </div>
  </section>
}
